/**
 * Save archive: enumerate, parse, export, import, duplicate, delete.
 *
 * Slots are directories below SAVEGAME; their count and managed ranges come
 * from the running engine. A populated slot holds SAVE.DAT plus one .SAV per visited
 * map, an AUTOMAP.SAV, a proto/ tree, and — on CE — ce.sav / sfallgv.sav.
 *
 * SAVE.DAT header, big-endian, as written by lsgSaveHeaderInSlot:
 *   0x00  24  signature "FALLOUT SAVE FILE"
 *   0x18   2  version major (1 on a stock save)
 *   0x1A   2  version minor (2 on a stock save)
 *   0x1C   1  release char ('R')
 *   0x1D  32  character name
 *   0x3D  30  save description (what the player typed)
 *   0x5B   2  wall-clock day    <- the writer emits day/month/year, while the
 *   0x5D   2  wall-clock month     reader assigns them month/day/year. That
 *   0x5F   2  wall-clock year      swap is an upstream quirk; we follow the
 *   0x61   4  wall-clock hour+min  bytes as written, so our dates are right.
 *   0x65   2  in-game month
 *   0x67   2  in-game day
 *   0x69   2  in-game year
 *   0x6B   4  in-game ticks
 *   0x6F   2  elevation
 *   0x71   2  map number
 *   0x73  16  current map file, e.g. "NCRENT.SAV"
 */

import type { EmFS } from './engine';
import { activeGameType, activeIdbfsDbName, activeSaveDir } from './config';
import { createZip, type ZipInput } from './zip/write';
import { readCentralDirectory, verifiedEntryBytes } from './zip/read';

export interface SaveTopology {
  totalSlots: number;
  slotsPerPage: number;
  quicksaveCount: number;
  autosaveStart: number;
  autosaveCount: number;
}

const FALLBACK_TOPOLOGY: SaveTopology = {
  totalSlots: 10,
  slotsPerPage: 10,
  quicksaveCount: 0,
  autosaveStart: 0,
  autosaveCount: 0,
};

let topology = FALLBACK_TOPOLOGY;

export function configureSaveTopology(next: SaveTopology): void {
  const totalSlots = Math.max(1, Math.min(100, Math.trunc(next.totalSlots)));
  const quicksaveCount = Math.max(0, Math.min(totalSlots, Math.trunc(next.quicksaveCount)));
  const autosaveCount = Math.max(0, Math.min(totalSlots, Math.trunc(next.autosaveCount)));
  const autosaveStart = autosaveCount
    ? Math.max(1, Math.min(totalSlots - autosaveCount + 1, Math.trunc(next.autosaveStart)))
    : 0;
  topology = {
    totalSlots,
    slotsPerPage: Math.max(1, Math.min(totalSlots, Math.trunc(next.slotsPerPage) || 10)),
    quicksaveCount,
    autosaveStart,
    autosaveCount,
  };
}

export const getSaveTopology = (): Readonly<SaveTopology> => topology;
export const slotCount = (): number => topology.totalSlots;
const SIGNATURE = 'FALLOUT SAVE FILE';

export interface SaveHeader {
  characterName: string;
  description: string;
  savedAt: Date | null;
  gameDate: { day: number; month: number; year: number } | null;
  mapFile: string;
  version: string;
}

export interface SlotInfo {
  slot: number; // 1-based
  dir: string;
  occupied: boolean;
  fileCount: number;
  bytes: number;
  mtime: Date | null;
  header: SaveHeader | null;
  /** Set when SAVE.DAT exists but could not be parsed. */
  error?: string;
  role: 'manual' | 'quicksave' | 'autosave';
}

export const slotDirName = (slot: number) => `SLOT${String(slot).padStart(2, '0')}`;
export const slotPath = (slot: number) => `${activeSaveDir()}/${slotDirName(slot)}`;

export function slotRole(slot: number): SlotInfo['role'] {
  if (topology.autosaveCount && slot >= topology.autosaveStart
      && slot < topology.autosaveStart + topology.autosaveCount) return 'autosave';
  if (slot <= topology.quicksaveCount) return 'quicksave';
  return 'manual';
}

function requireSlot(slot: number): void {
  if (!Number.isInteger(slot) || slot < 1 || slot > topology.totalSlots) {
    throw new Error(`Slot must be between 1 and ${topology.totalSlots}.`);
  }
}

function cstr(bytes: Uint8Array, off: number, len: number): string {
  let end = off;
  const max = off + len;
  while (end < max && bytes[end] !== 0) end++;
  return new TextDecoder('latin1').decode(bytes.subarray(off, end)).trim();
}

export function parseSaveHeader(bytes: Uint8Array): SaveHeader {
  if (bytes.length < 0x85) throw new Error('SAVE.DAT is truncated');
  const sig = cstr(bytes, 0, 24);
  if (!sig.startsWith(SIGNATURE)) throw new Error('SAVE.DAT signature mismatch');
  const v = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

  const day = v.getUint16(0x5b);
  const month = v.getUint16(0x5d);
  const year = v.getUint16(0x5f);
  // 0x61 holds hour+minute summed into one int by the engine, so the clock time
  // is not recoverable; we surface the date only, plus the directory mtime.
  const valid = month >= 1 && month <= 12 && day >= 1 && day <= 31 && year > 1990 && year < 2200;

  return {
    // lsgSaveHeaderInSlot writes VERSION_MAJOR at 0x18 and VERSION_MINOR at
    // 0x1a. The native LoadSaveSlotData field names are historically swapped,
    // so a stock save renders as "1.2R".
    version: `${v.getUint16(0x18)}.${v.getUint16(0x1a)}${String.fromCharCode(bytes[0x1c] || 32).trim()}`,
    characterName: cstr(bytes, 0x1d, 32),
    description: cstr(bytes, 0x3d, 30),
    savedAt: valid ? new Date(year, month - 1, day) : null,
    gameDate: { month: v.getUint16(0x65), day: v.getUint16(0x67), year: v.getUint16(0x69) },
    mapFile: cstr(bytes, 0x73, 16),
  };
}

function walk(FS: EmFS, dir: string, base = ''): { path: string; size: number; mtime: Date }[] {
  const out: { path: string; size: number; mtime: Date }[] = [];
  let names: string[];
  try {
    names = FS.readdir(dir);
  } catch {
    return out;
  }
  for (const name of names) {
    if (name === '.' || name === '..') continue;
    const full = `${dir}/${name}`;
    const rel = base ? `${base}/${name}` : name;
    let st;
    try {
      st = FS.stat(full);
    } catch {
      continue;
    }
    if (FS.isDir(st.mode)) out.push(...walk(FS, full, rel));
    else out.push({ path: rel, size: st.size, mtime: st.mtime });
  }
  return out;
}

export function listSlots(FS: EmFS): SlotInfo[] {
  const slots: SlotInfo[] = [];
  for (let slot = 1; slot <= topology.totalSlots; slot++) {
    const dir = slotPath(slot);
    const files = walk(FS, dir);
    const info: SlotInfo = {
      slot,
      dir,
      occupied: false,
      fileCount: files.length,
      bytes: files.reduce((n, f) => n + f.size, 0),
      mtime: files.length ? new Date(Math.max(...files.map((f) => f.mtime.getTime()))) : null,
      header: null,
      role: slotRole(slot),
    };
    const saveDat = files.find((f) => f.path.toUpperCase() === 'SAVE.DAT');
    if (saveDat) {
      info.occupied = true;
      try {
        info.header = parseSaveHeader(FS.readFile(`${dir}/SAVE.DAT`, { encoding: 'binary' }) as Uint8Array);
      } catch (e) {
        info.error = e instanceof Error ? e.message : String(e);
      }
    }
    slots.push(info);
  }
  return slots;
}

/** Cheap change signature — lets the shell refresh the archive UI only when it matters. */
export function slotsSignature(FS: EmFS): string {
  const parts: string[] = [];
  for (let slot = 1; slot <= topology.totalSlots; slot++) {
    for (const f of walk(FS, slotPath(slot))) parts.push(`${slot}/${f.path}:${f.size}:${f.mtime.getTime()}`);
  }
  return parts.join('|');
}

export async function exportSlot(FS: EmFS, slot: number): Promise<{ blob: Blob; filename: string }> {
  requireSlot(slot);
  const dir = slotPath(slot);
  const files = walk(FS, dir);
  if (!files.length) throw new Error(`Slot ${slot} is empty`);
  const inputs: ZipInput[] = files.map((f) => ({
    name: f.path,
    data: FS.readFile(`${dir}/${f.path}`, { encoding: 'binary' }) as Uint8Array,
    lastModified: f.mtime.getTime(),
  }));
  const blob = await createZip(inputs);

  let label = `slot${String(slot).padStart(2, '0')}`;
  const saveDat = inputs.find((i) => i.name.toUpperCase() === 'SAVE.DAT');
  if (saveDat) {
    try {
      const h = parseSaveHeader(saveDat.data);
      const safe = (s: string) => s.replace(/[^A-Za-z0-9._-]+/g, '-').replace(/^-|-$/g, '');
      label = [safe(h.characterName) || 'save', safe(h.description)].filter(Boolean).join('_').slice(0, 60) || label;
    } catch {
      /* keep the slot-number label */
    }
  }
  const stamp = new Date().toISOString().slice(0, 10);
  const prefix = activeGameType() === 'fo1' ? 'f1' : 'f2';
  return { blob, filename: `${prefix}-${label}-${stamp}.zip` };
}

interface PersistedIdbfsEntry {
  timestamp?: Date | string | number;
  contents?: Uint8Array | ArrayBuffer;
}

/**
 * Every file the browser holds under SAVEGAME, read without booting the engine.
 *
 * The launcher can therefore offer a real recovery action immediately beside
 * "Erase everything", and the vault backup can be taken from the gate. IDBFS
 * stores the mounted SAVEGAME tree by absolute path; directories have no
 * `contents`, while files carry a typed array.
 *
 * @param keep  path filter, relative to SAVEGAME. Defaults to slot directories.
 */
export async function readPersistedSaveFiles(
  keep: (relativePath: string) => boolean = (path) => /^SLOT\d+\//i.test(path)
): Promise<ZipInput[]> {
  const saveRoot = activeSaveDir();
  const db = await new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(activeIdbfsDbName());
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Could not open the browser save archive.'));
  });

  try {
    if (!db.objectStoreNames.contains('FILE_DATA')) throw new Error('There are no browser saves to export yet.');
    return await new Promise<ZipInput[]>((resolve, reject) => {
      const out: ZipInput[] = [];
      const transaction = db.transaction('FILE_DATA', 'readonly');
      const cursorRequest = transaction.objectStore('FILE_DATA').openCursor();
      cursorRequest.onsuccess = () => {
        const cursor = cursorRequest.result;
        if (!cursor) return;
        const path = String(cursor.key);
        const entry = cursor.value as PersistedIdbfsEntry;
        const prefix = `${saveRoot}/`;
        if (path.startsWith(prefix) && entry.contents) {
          const relative = path.slice(prefix.length);
          if (keep(relative)) {
            const data = entry.contents instanceof Uint8Array
              ? entry.contents
              : new Uint8Array(entry.contents);
            const timestamp = entry.timestamp ? new Date(entry.timestamp).getTime() : Date.now();
            out.push({ name: relative, data, lastModified: Number.isFinite(timestamp) ? timestamp : Date.now() });
          }
        }
        cursor.continue();
      };
      cursorRequest.onerror = () => reject(cursorRequest.error ?? new Error('Could not read the browser save archive.'));
      transaction.oncomplete = () => resolve(out);
      transaction.onerror = () => reject(transaction.error ?? new Error('Could not read the browser save archive.'));
      transaction.onabort = () => reject(transaction.error ?? new Error('Reading the browser save archive was interrupted.'));
    });
  } finally {
    db.close();
  }
}

/** The plain per-slot .zip export, as offered beside "Erase everything". */
export async function exportPersistedSaveArchive(): Promise<{
  blob: Blob;
  filename: string;
  fileCount: number;
  slotCount: number;
}> {
  const inputs = await readPersistedSaveFiles();
  if (!inputs.length) throw new Error('There are no browser saves to export yet.');
  const slots = new Set(inputs.map((input) => input.name.split('/')[0].toUpperCase()));
  const blob = await createZip(inputs);
  const stamp = new Date().toISOString().slice(0, 10);
  return {
    blob,
    filename: `rerun-reactor-saves-${stamp}.zip`,
    fileCount: inputs.length,
    slotCount: slots.size,
  };
}

function removeTree(FS: EmFS, path: string): void {
    let names: string[];
    try {
      names = FS.readdir(path);
    } catch {
      return;
    }
    for (const name of names) {
      if (name === '.' || name === '..') continue;
      const full = `${path}/${name}`;
      const st = FS.stat(full);
      if (FS.isDir(st.mode)) removeTree(FS, full);
      else FS.unlink(full);
    }
    try {
      FS.rmdir(path);
    } catch {
      /* mount point / already gone */
    }
}

/** Finish or discard a slot swap interrupted by a tab/process crash. */
export function recoverSaveTransactions(FS: EmFS): boolean {
  const saveRoot = activeSaveDir();
  let names: string[];
  try { names = FS.readdir(saveRoot); } catch { return false; }
  let changed = false;
  for (const name of names) {
    if (name.startsWith('.staged-SLOT')) {
      removeTree(FS, `${saveRoot}/${name}`);
      changed = true;
      continue;
    }
    const match = /^\.rollback-(SLOT\d+)-/.exec(name);
    if (!match) continue;
    const rollback = `${saveRoot}/${name}`;
    const target = `${saveRoot}/${match[1]}`;
    if (!FS.analyzePath(target).exists) {
      FS.rename(rollback, target);
    } else {
      removeTree(FS, rollback);
    }
    changed = true;
  }
  return changed;
}

export function deleteSlot(FS: EmFS, slot: number): void {
  requireSlot(slot);
  const dir = slotPath(slot);
  removeTree(FS, dir);
  try {
    FS.mkdir(dir);
  } catch {
    /* fine */
  }
}

function mkdirp(FS: EmFS, path: string) {
  const parts = path.split('/').filter(Boolean);
  let cur = '';
  for (const p of parts) {
    cur += '/' + p;
    try {
      FS.mkdir(cur);
    } catch {
      /* exists */
    }
  }
}

export function duplicateSlot(FS: EmFS, from: number, to: number): void {
  requireSlot(from);
  requireSlot(to);
  if (from === to) throw new Error('Source and destination slots must be different.');
  const src = slotPath(from);
  const files = walk(FS, src);
  if (!files.length) throw new Error(`Slot ${from} is empty`);
  replaceSlotFiles(FS, to, files.map((f) => ({
    path: f.path,
    bytes: FS.readFile(`${src}/${f.path}`, { encoding: 'binary' }) as Uint8Array,
  })));
}

export type SlotSnapshot = { path: string; bytes: Uint8Array }[];

export function snapshotSlot(FS: EmFS, slot: number): SlotSnapshot {
  requireSlot(slot);
  const dir = slotPath(slot);
  return walk(FS, dir).map((f) => ({
    path: f.path,
    bytes: Uint8Array.from(FS.readFile(`${dir}/${f.path}`, { encoding: 'binary' }) as Uint8Array),
  }));
}

export function restoreSlotSnapshot(FS: EmFS, slot: number, snapshot: SlotSnapshot): void {
  requireSlot(slot);
  replaceSlotFiles(FS, slot, snapshot);
}

function replaceSlotFiles(FS: EmFS, slot: number, files: SlotSnapshot): void {
  const target = slotPath(slot);
  const nonce = `${slotDirName(slot)}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  const saveRoot = activeSaveDir();
  const staged = `${saveRoot}/.staged-${nonce}`;
  const rollback = `${saveRoot}/.rollback-${nonce}`;
  removeTree(FS, staged);
  removeTree(FS, rollback);
  mkdirp(FS, staged);
  try {
    for (const file of files) {
      const path = `${staged}/${file.path}`;
      mkdirp(FS, path.slice(0, path.lastIndexOf('/')));
      FS.writeFile(path, file.bytes);
    }
    const hadTarget = walk(FS, target).length > 0;
    if (hadTarget) FS.rename(target, rollback);
    else removeTree(FS, target);
    try {
      FS.rename(staged, target);
    } catch (e) {
      if (hadTarget) FS.rename(rollback, target);
      throw e;
    }
    removeTree(FS, rollback);
  } catch (e) {
    removeTree(FS, staged);
    throw e;
  }
}

let mutationTail: Promise<void> = Promise.resolve();

/** Serialize shell-side slot changes so IDBFS never sees two overlapping mutations. */
export function queueSaveMutation<T>(operation: () => Promise<T> | T): Promise<T> {
  const run = mutationTail.then(operation, operation);
  mutationTail = run.then(() => undefined, () => undefined);
  return run;
}

/**
 * Splits a zip entry name into path components, refusing anything that could
 * escape the directory we are about to write into. Absolute paths, drive
 * letters, `..`, empty segments and control characters are all rejected outright
 * rather than sanitised — a save archive has no legitimate use for any of them.
 *
 * Shared by the per-slot importer and the vault-backup restore, so both refuse
 * the same shapes.
 */
export function safeArchiveParts(name: string): string[] {
  if (name.length > 512 || /[\0-\x1f]/.test(name) || name.startsWith('/') || /^[A-Za-z]:/.test(name)) {
    throw new Error(`Unsafe archive path: ${name}`);
  }
  const parts = name.split('/');
  if (parts.some((part) => !part || part === '.' || part === '..')) {
    throw new Error(`Unsafe archive path: ${name}`);
  }
  return parts;
}

/**
 * Import a previously exported .zip into a slot. Tolerates archives whose
 * entries are nested one level deep (SLOT03/SAVE.DAT), which is what you get
 * from zipping a slot folder in Finder or Explorer.
 */
export async function importSlotZip(FS: EmFS, slot: number, file: Blob): Promise<number> {
  requireSlot(slot);
  const entries = (await readCentralDirectory(file)).filter(
    (e) => !e.isDirectory && !e.name.split('/').some((p) => p === '__MACOSX' || p.startsWith('._'))
  );
  if (!entries.length) throw new Error('Archive contains no files.');
  if (entries.length > 4096) throw new Error(`Archive has too many files (${entries.length}; limit 4096).`);

  const clean = entries.map((entry) => {
    const parts = safeArchiveParts(entry.name);
    if (!Number.isSafeInteger(entry.compressedSize) || entry.compressedSize < 0) {
      throw new Error(`Invalid compressed size for ${entry.name}.`);
    }
    if (entry.uncompressedSize > 0 && entry.compressedSize === 0) {
      throw new Error(`Invalid compression ratio for ${entry.name}.`);
    }
    // No per-file ratio cap: sfallgv.sav and AUTOMAP.SAV are mostly zeros and
    // deflate far past any sane ratio. Zip bombs are bounded by the per-file
    // and total uncompressed limits below instead.
    return { entry, parts };
  });

  let strip = 0;
  const hasSaveDat = (depth: number) =>
    clean.some(({ parts }) => parts.slice(depth).join('/').toUpperCase() === 'SAVE.DAT');
  if (!hasSaveDat(0) && hasSaveDat(1)) strip = 1;
  if (!hasSaveDat(strip))
    throw new Error('Archive does not look like a Fallout save (no SAVE.DAT at its root).');

  if (strip && !clean.every(({ parts }) => parts.length > 1 && parts[0] === clean[0]!.parts[0])) {
    throw new Error('Archive mixes a wrapped slot folder with files outside it.');
  }

  const MAX_FILE_BYTES = 64 * 1024 * 1024;
  const MAX_TOTAL_BYTES = 256 * 1024 * 1024;
  const seen = new Set<string>();
  const files: SlotSnapshot = [];
  let total = 0;
  for (const { entry, parts } of clean) {
    const rel = parts.slice(strip).join('/');
    if (!rel) continue;
    const key = rel.toLocaleLowerCase('en-US');
    if (seen.has(key)) throw new Error(`Archive contains a duplicate path: ${rel}`);
    seen.add(key);
    total += entry.uncompressedSize;
    if (!Number.isSafeInteger(total) || total > MAX_TOTAL_BYTES) {
      throw new Error(`Archive expands beyond the ${MAX_TOTAL_BYTES / 1024 / 1024} MiB save limit.`);
    }
    files.push({ path: rel, bytes: await verifiedEntryBytes(file, entry, MAX_FILE_BYTES) });
  }

  const saveDat = files.find((entry) => entry.path.toUpperCase() === 'SAVE.DAT');
  if (!saveDat) throw new Error('Archive does not contain SAVE.DAT after path normalization.');
  const header = parseSaveHeader(saveDat.bytes);
  // Each engine's lsgLoadHeaderInSlot refuses anything but its own version, and
  // the two games do not agree: fallout1-ce writes 1.1R, fallout2-ce 1.2R. An
  // archive from the other game would import cleanly and then fail to load, so
  // it is refused here where we can say why.
  const expected = activeGameType() === 'fo1' ? '1.1R' : '1.2R';
  if (header.version !== expected) {
    throw new Error(`The engine cannot load save version ${header.version}; expected ${expected}.`);
  }
  replaceSlotFiles(FS, slot, files);
  return files.length;
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}
